import { useEffect, useRef, useState } from 'react'

import { type PrimaryCategory } from '@webapp/core/entities/internal/PrimaryCategory'
import { type ReportAccessGrant } from '@webapp/platform/iam/entities/ReportAccessGrant'
import { type User } from '@webapp/platform/iam/entities/User'
import { useClearSecondaryCategoryReportAccessPresenter } from '@webapp/platform/iam/features/clear-secondary-category-report-access/presenters/UseClearSecondaryCategoryReportAccessPresenter'
import { useFirstPrimaryCategoryPresenter } from '@webapp/platform/iam/features/get-primary-category-list/presenters/UseFirstPrimaryCategoryPresenter'
import { useGrantReportAccessPresenter } from '@webapp/platform/iam/features/grant-report-access/presenters/UseGrantReportAccessPresenter'
import { useGrantReportAccessToAllPrimaryCategoriesPresenter } from '@webapp/platform/iam/features/grant-report-access-to-all-primary-categories/presenters/UseGrantReportAccessToAllPrimaryCategoriesPresenter'
import { useGrantReportAccessToAllSecondaryCategoriesPresenter } from '@webapp/platform/iam/features/grant-report-access-to-all-secondary-categories/presenters/UseGrantReportAccessToAllSecondaryCategoriesPresenter'
import { useFirstReportAccessSecondaryCategoryPresenter } from '@webapp/platform/iam/features/list-report-access-secondary-categories/presenters/UseFirstReportAccessSecondaryCategoryPresenter'
import { useRevokeReportAccessFromAllPrimaryCategoriesPresenter } from '@webapp/platform/iam/features/revoke-report-access-from-all-primary-categories/presenters/UseRevokeReportAccessFromAllPrimaryCategoriesPresenter'
import { useRevokeReportAccessGrantPresenter } from '@webapp/platform/iam/features/revoke-report-access-grant/presenters/UseRevokeReportAccessGrantPresenter'
import { type SecondaryCategoryConfig } from '@webapp/platform/organization-management/entities/SecondaryCategoryConfig'
import {
  type ReportAccessDetailModeEnum,
  ReportAccessDetailModes,
  type ReportAccessDetailPaneStateEnum,
  ReportAccessDetailPaneStates,
  type ReportAccessSubmitActionEnum,
  ReportAccessSubmitActions,
} from './enums'

const HIGHLIGHT_DURATION_MS = 1000
const INITIAL_SECONDARY_CATEGORY_PAGING = { page: 1, defaultPage: 1 }

export const useReportAccessView = () => {
  const [selectedPrimaryCategory, setSelectedPrimaryCategory] = useState<
    PrimaryCategory | undefined
  >(undefined)
  const [isAllPrimaryCategories, setIsAllPrimaryCategories] = useState(false)
  const [selectedSecondaryCategory, setSelectedSecondaryCategory] = useState<
    SecondaryCategoryConfig | undefined
  >(undefined)
  const [isAllSecondaryCategories, setIsAllSecondaryCategories] =
    useState(false)
  // `page` is the list page on screen; `defaultPage` is the page whose first
  // SecondaryCategory is the selection while none is picked. A bulk grant
  // moves the default to the page on screen (BHV-021); paging alone doesn't.
  const [secondaryCategoryPaging, setSecondaryCategoryPaging] = useState(
    INITIAL_SECONDARY_CATEGORY_PAGING
  )
  const [submitAction, setSubmitAction] =
    useState<ReportAccessSubmitActionEnum>(ReportAccessSubmitActions.ADD)

  const [highlightedGrantId, setHighlightedGrantId] = useState<
    number | undefined
  >(undefined)
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout>>()

  const [isClearAllConfirmOpen, setIsClearAllConfirmOpen] = useState(false)
  // The pending payloads outlive their dialog's close so the copy holds
  // through the exit fade; every open overwrites them first
  const [pendingClearAllCount, setPendingClearAllCount] = useState(0)
  const [isBulkRemoveConfirmOpen, setIsBulkRemoveConfirmOpen] = useState(false)
  const [userPickerFocusRequest, setUserPickerFocusRequest] = useState(0)
  const [pendingBulkRemoveUser, setPendingBulkRemoveUser] = useState<
    User | undefined
  >(undefined)

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    }
  }, [])

  const highlightGrant = (grantId: number) => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    setHighlightedGrantId(grantId)
    highlightTimerRef.current = setTimeout(() => {
      setHighlightedGrantId(undefined)
    }, HIGHLIGHT_DURATION_MS)
  }

  const {
    data: { firstPrimaryCategory },
    states: {
      isFetching: isFetchingFirstPrimaryCategory,
      isError: isFirstPrimaryCategoryError,
      isEmpty: isTenantWithoutPrimaryCategories,
    },
  } = useFirstPrimaryCategoryPresenter()

  // Auto-select-first (BHV-002): an explicit pick wins; otherwise the
  // tenant's first PrimaryCategory is the selection. ALL mode has none.
  const effectivePrimaryCategory = isAllPrimaryCategories
    ? undefined
    : (selectedPrimaryCategory ?? firstPrimaryCategory)

  const {
    data: { firstSecondaryCategoryConfig },
    states: {
      isLoading: isLoadingFirstSecondaryCategory,
      isEmpty: isPrimaryCategoryWithoutSecondaryCategories,
      isError: isFirstSecondaryCategoryError,
    },
  } = useFirstReportAccessSecondaryCategoryPresenter({
    primaryCategoryId: effectivePrimaryCategory?.id,
    page: secondaryCategoryPaging.defaultPage,
  })

  // Auto-select-first (BHV-007): an explicit pick wins; otherwise the first
  // SecondaryCategory on the default page is the selection
  const effectiveSecondaryCategory =
    selectedSecondaryCategory ?? firstSecondaryCategoryConfig

  const getDetailMode = (): ReportAccessDetailModeEnum => {
    if (isAllPrimaryCategories) {
      return ReportAccessDetailModes.ALL_PRIMARY_CATEGORIES
    }
    if (isAllSecondaryCategories) {
      return ReportAccessDetailModes.ALL_SECONDARY_CATEGORIES
    }
    return ReportAccessDetailModes.SINGLE_SECONDARY_CATEGORY
  }
  const detailMode = getDetailMode()

  const isResolvingPrimaryCategory =
    !isAllPrimaryCategories &&
    !effectivePrimaryCategory &&
    isFetchingFirstPrimaryCategory

  const getDetailPaneState = (): ReportAccessDetailPaneStateEnum => {
    if (detailMode === ReportAccessDetailModes.ALL_PRIMARY_CATEGORIES) {
      return ReportAccessDetailPaneStates.READY
    }
    if (isResolvingPrimaryCategory) return ReportAccessDetailPaneStates.LOADING
    if (!effectivePrimaryCategory && isFirstPrimaryCategoryError) {
      return ReportAccessDetailPaneStates.PRIMARY_CATEGORY_ERROR
    }
    if (!effectivePrimaryCategory && isTenantWithoutPrimaryCategories) {
      return ReportAccessDetailPaneStates.NO_PRIMARY_CATEGORIES
    }
    if (
      detailMode === ReportAccessDetailModes.ALL_SECONDARY_CATEGORIES &&
      effectivePrimaryCategory
    ) {
      return ReportAccessDetailPaneStates.READY
    }
    if (effectiveSecondaryCategory) return ReportAccessDetailPaneStates.READY
    if (isLoadingFirstSecondaryCategory) {
      return ReportAccessDetailPaneStates.LOADING
    }
    if (isPrimaryCategoryWithoutSecondaryCategories) {
      return ReportAccessDetailPaneStates.NO_SECONDARY_CATEGORIES
    }
    if (isFirstSecondaryCategoryError) {
      return ReportAccessDetailPaneStates.SECONDARY_CATEGORIES_ERROR
    }
    return ReportAccessDetailPaneStates.NO_SECONDARY_CATEGORY
  }

  const {
    commands: { grant },
    states: { isGranting },
  } = useGrantReportAccessPresenter()

  const {
    commands: { revoke },
    states: { isRevoking },
  } = useRevokeReportAccessGrantPresenter()

  const {
    commands: { clearAll },
    states: { isClearing },
  } = useClearSecondaryCategoryReportAccessPresenter()

  const {
    commands: { grantToAllSecondaryCategories },
    states: { isGranting: isGrantingToAllSecondaryCategories },
  } = useGrantReportAccessToAllSecondaryCategoriesPresenter()

  const {
    commands: { grantToAllPrimaryCategories },
    states: { isGranting: isGrantingToAllPrimaryCategories },
  } = useGrantReportAccessToAllPrimaryCategoriesPresenter()

  const {
    commands: { revokeFromAllPrimaryCategories },
    states: { isRevoking: isBulkRemoving },
  } = useRevokeReportAccessFromAllPrimaryCategoriesPresenter()

  const isSubmitting =
    isGranting ||
    isGrantingToAllSecondaryCategories ||
    isGrantingToAllPrimaryCategories ||
    isBulkRemoving

  const requestUserPickerFocus = () => {
    setUserPickerFocusRequest((request) => request + 1)
  }

  // Mode state machine — real PrimaryCategory selection (BHV-004)
  const handleSelectPrimaryCategory = (primaryCategory: PrimaryCategory) => {
    setSelectedPrimaryCategory(primaryCategory)
    setIsAllPrimaryCategories(false)
    setIsAllSecondaryCategories(false)
    setSelectedSecondaryCategory(undefined)
    setSecondaryCategoryPaging(INITIAL_SECONDARY_CATEGORY_PAGING)
    setSubmitAction(ReportAccessSubmitActions.ADD)
    setHighlightedGrantId(undefined)
  }

  // Mode state machine — "ALL" option (BHV-005); the left pane unmounts
  const handleSelectAllPrimaryCategories = () => {
    setIsAllPrimaryCategories(true)
    setIsAllSecondaryCategories(false)
    setSubmitAction(ReportAccessSubmitActions.ADD)
  }

  const handleSelectSecondaryCategory = (config: SecondaryCategoryConfig) => {
    setIsAllSecondaryCategories(false)
    setHighlightedGrantId(undefined)
    setSelectedSecondaryCategory(config)
  }

  const handleToggleSelectAllSecondaryCategories = () => {
    setIsAllSecondaryCategories((isAllSelected) => !isAllSelected)
  }

  const handleExitSelectAllSecondaryCategories = () => {
    setIsAllSecondaryCategories(false)
  }

  const handleSecondaryCategoryPageChange = (page: number) => {
    setSecondaryCategoryPaging((paging) =>
      paging.page === page ? paging : { ...paging, page }
    )
  }

  // The one rule that makes Submit destructive (BHV-024, BHV-025)
  const isRemoveAction =
    isAllPrimaryCategories && submitAction === ReportAccessSubmitActions.REMOVE

  // Single picker dispatch — one handler, four commands by mode
  const handleSubmitUser = async (user: User) => {
    if (isAllPrimaryCategories) {
      if (isRemoveAction) {
        setPendingBulkRemoveUser(user)
        setIsBulkRemoveConfirmOpen(true)
        return
      }

      try {
        await grantToAllPrimaryCategories(user) // BHV-023
      } catch {
        // Error toast shown by the feature
      }
      requestUserPickerFocus()
      return
    }

    if (isAllSecondaryCategories) {
      if (!effectivePrimaryCategory) return

      try {
        await grantToAllSecondaryCategories(effectivePrimaryCategory, user)

        // Post-bulk reset (BHV-021): drop the explicit pick so the selection
        // falls back to the first SecondaryCategory of the page on screen.
        // Leave all mode last: these updates aren't batched after an await,
        // and an earlier exit would mount the grants list for a stale pick.
        setSecondaryCategoryPaging((paging) => ({
          ...paging,
          defaultPage: paging.page,
        }))
        setSelectedSecondaryCategory(undefined)
        setIsAllSecondaryCategories(false)
      } catch {
        // Error toast shown by the feature
      }
      requestUserPickerFocus()
      return
    }

    if (!effectiveSecondaryCategory) return

    try {
      const createdGrant = await grant(user, effectiveSecondaryCategory.id)
      highlightGrant(createdGrant.id) // BHV-013
    } catch {
      // Error toast shown by the feature
    }
    requestUserPickerFocus()
  }

  // Immediate optimistic revoke — no confirmation (BHV-017)
  const handleRemoveGrant = async (
    grantToRemove: ReportAccessGrant,
    { isLastGrant }: { isLastGrant: boolean }
  ) => {
    if (!effectiveSecondaryCategory) return

    // The list hands focus to a neighbouring tag; with none left, the picker
    if (isLastGrant) requestUserPickerFocus()

    try {
      await revoke(grantToRemove, effectiveSecondaryCategory.id)
    } catch {
      // The Model restored the tag; re-highlight it (BHV-018)
      highlightGrant(grantToRemove.id)
    }
  }

  // Clear all — n=0 is a no-op (BHV-020); n>0 opens the danger dialog (BHV-019)
  const handleClearAllRequest = (grants: ReportAccessGrant[]) => {
    if (grants.length === 0) return
    setPendingClearAllCount(grants.length)
    setIsClearAllConfirmOpen(true)
  }

  const closeClearAllConfirm = () => {
    setIsClearAllConfirmOpen(false)
  }

  const handleClearAllConfirm = async (done: () => void) => {
    if (!effectiveSecondaryCategory) {
      done()
      closeClearAllConfirm()
      return
    }

    try {
      await clearAll(effectiveSecondaryCategory.id)
    } catch {
      // Error toast shown by the feature
    }

    done()
    closeClearAllConfirm()
    // "Clear all (0)" is disabled now, so the dialog can't return focus to it
    requestUserPickerFocus()
  }

  // Submit, the dialog's trigger, is disabled once the picker is cleared
  const handleBulkRemoveDismiss = () => {
    setIsBulkRemoveConfirmOpen(false)
    requestUserPickerFocus()
  }

  const handleBulkRemoveConfirm = async (done: () => void) => {
    if (!pendingBulkRemoveUser) {
      done()
      handleBulkRemoveDismiss()
      return
    }

    try {
      await revokeFromAllPrimaryCategories(pendingBulkRemoveUser)
      done()
      handleBulkRemoveDismiss()
      setSubmitAction(ReportAccessSubmitActions.ADD) // BHV-025
    } catch {
      // Error toast shown by the feature
      done()
      handleBulkRemoveDismiss()
    }
  }

  // Without a PrimaryCategory there is no SecondaryCategory list to show,
  // so the detail card takes the full row
  const showSecondaryCategoryPane =
    detailMode !== ReportAccessDetailModes.ALL_PRIMARY_CATEGORIES &&
    (Boolean(effectivePrimaryCategory) || isResolvingPrimaryCategory)

  return {
    states: {
      selectedPrimaryCategory: effectivePrimaryCategory,
      isAllPrimaryCategories,
      selectedSecondaryCategory: effectiveSecondaryCategory,
      isAllSecondaryCategories,
      secondaryCategoryPage: secondaryCategoryPaging.page,
      detailMode,
      detailPaneState: getDetailPaneState(),
      detailPaneWidth: showSecondaryCategoryPane ? 2 / 3 : 1,
      showSecondaryCategoryPane,
      // Grants list renders in single-SecondaryCategory mode only (BHV-011)
      grantListSecondaryCategoryId:
        detailMode === ReportAccessDetailModes.SINGLE_SECONDARY_CATEGORY
          ? effectiveSecondaryCategory?.id
          : undefined,
      submitAction,
      isRemoveAction,
      highlightedGrantId,
      isClearAllConfirmOpen,
      isBulkRemoveConfirmOpen,
      pendingClearAllCount,
      pendingBulkRemoveUser,
      isSubmitting,
      // A grant made while clear-all is in flight could be wiped by it
      isSubmitDisabled: isSubmitting || isClearing,
      // ...and a clear-all racing a grant or revoke could be undone by it
      isGrantCommandPending: isSubmitting || isRevoking,
      userPickerFocusRequest,
    },
    commands: {
      onSubmitUser: handleSubmitUser,
      onRemoveGrant: handleRemoveGrant,
      onClearAllConfirm: handleClearAllConfirm,
      onBulkRemoveConfirm: handleBulkRemoveConfirm,
    },
    methods: {
      onSelectPrimaryCategory: handleSelectPrimaryCategory,
      onSelectAllPrimaryCategories: handleSelectAllPrimaryCategories,
      onSelectSecondaryCategory: handleSelectSecondaryCategory,
      onToggleSelectAllSecondaryCategories:
        handleToggleSelectAllSecondaryCategories,
      onExitSelectAllSecondaryCategories:
        handleExitSelectAllSecondaryCategories,
      onSecondaryCategoryPageChange: handleSecondaryCategoryPageChange,
      onSubmitActionChange: setSubmitAction,
      onClearAllRequest: handleClearAllRequest,
      onClearAllDismiss: closeClearAllConfirm,
      onBulkRemoveDismiss: handleBulkRemoveDismiss,
      // "Try again" unmounts with the grants error; the picker stays
      onGrantListRetry: requestUserPickerFocus,
    },
  }
}
